import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Boxes, Handshake, ShieldCheck, Truck } from "lucide-react";
import Seo from "@/components/Seo";

const Benefit = ({ icon: Icon, title, text }) => (
  <div className="bg-white border border-stone-200 p-7 rounded-md lift">
    <div className="w-11 h-11 rounded-full bg-emerald-800/10 text-emerald-800 flex items-center justify-center">
      <Icon size={20} strokeWidth={1.5} />
    </div>
    <div className="mt-5 font-serif text-xl text-stone-900">{title}</div>
    <p className="mt-2 text-sm text-stone-600 font-sans leading-relaxed">{text}</p>
  </div>
);

export default function Wholesale() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    business_name: "",
    business_type: "store",
    expected_volume: "",
    contact_phone: "",
    state: user?.state || "",
    notes: "",
  });

  useEffect(() => {
    api.get("/states").then((r) => setStates(r.data));
  }, []);

  const set = (k) => (v) => setForm({ ...form, [k]: v?.target ? v.target.value : v });

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.info("Sign in to apply for wholesale.");
      navigate("/login");
      return;
    }
    if (!form.business_name || !form.expected_volume || !form.contact_phone || !form.state) {
      toast.error("Please fill in business name, volume, phone, and state.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/wholesale/apply", form);
      toast.success("Wholesale application submitted — our admins will reach out.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Seo
        url="/wholesale"
        title="Wholesale Onboarding"
        description="Bulk orders direct from hobby farms. For stores, restaurants, families pooling together, and community groups. Verified farms, real contracts."
        image="https://azuregypsy.org/og-wholesale.jpg"
      />
      <section className="relative">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1542838132-92c53300491e?w=1600&q=80"
            alt="Wholesale produce crates being loaded"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/55 via-stone-900/30 to-stone-50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 pt-28 pb-32 sm:pt-36 sm:pb-40">
          <div className="text-[11px] uppercase tracking-[0.4em] text-amber-200 font-sans">Wholesale Onboarding</div>
          <h1 className="mt-5 font-serif text-5xl sm:text-6xl text-stone-50 leading-[1.05] max-w-3xl">
            Bulk orders, direct from hobby farms.
          </h1>
          <p className="mt-6 font-sans text-stone-100/90 text-lg max-w-2xl">
            For stores, restaurants, families pooling together and community groups. Direct contracts with verified hobby farmers in your state — no middlemen.
          </p>
          <a href="#apply">
            <Button data-testid="wholesale-hero-cta" className="mt-8 bg-emerald-800 hover:bg-emerald-700 text-stone-50 px-7 py-6 rounded-md">
              Apply Now
            </Button>
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-20">
        <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Why Wholesale With Us</div>
        <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-stone-900 max-w-3xl">Stocking your shelves, feeding your family — straight from the source.</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <Benefit icon={Boxes} title="Bulk pricing direct" text="Skip the distributor. Lug pricing on greens, half-pallets on root vegetables, quarters and halves on meats — direct from the farm." />
          <Benefit icon={Handshake} title="Real contracts" text="Lock in weekly or seasonal supply with the hobby farmer of your choice. We handle the paperwork, you handle the kitchen." />
          <Benefit icon={ShieldCheck} title="Verified hobby farms" text="Every farm we work with is ID-verified by our admin team. Real growers, real food, real provenance." />
          <Benefit icon={Truck} title="State-level routes" text="Local pickup zones with our network of drivers, or arrange your own pickup at the farm gate." />
          <Benefit icon={Building2} title="Three onboarding tracks" text="Whether you're a store, a family group, or an individual — we tailor your onboarding to your buying pattern." />
          <Benefit icon={Handshake} title="Admin-tracked" text="Your account is monitored by Azure Gypsy admins so larger orders get priority routing and proactive sourcing." />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-12 pb-12">
        <div className="bg-stone-900 rounded-md p-10 sm:p-14 grid md:grid-cols-3 gap-8 items-start">
          {[
            ["1", "Apply", "Tell us about your business, expected weekly volume, and your state."],
            ["2", "Connect", "Our admins match you with verified hobby farmers who can fulfill your volume."],
            ["3", "Order", "Direct ordering opens in your dashboard. Repeat weekly, monthly, or seasonally."],
          ].map(([n, t, d]) => (
            <div key={n}>
              <div className="w-10 h-10 rounded-full bg-amber-700 text-stone-50 flex items-center justify-center font-serif text-lg">{n}</div>
              <div className="mt-4 font-serif text-2xl text-stone-50">{t}</div>
              <p className="mt-2 text-sm text-stone-300 font-sans leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="apply" className="max-w-3xl mx-auto px-6 sm:px-12 pb-24">
        <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Apply</div>
        <h2 className="mt-3 font-serif text-4xl text-stone-900">Wholesale Application</h2>
        <p className="mt-3 font-sans text-stone-600">
          Our admin team typically reaches out within two business days. You'll need a free Azure Gypsy account to submit.
        </p>

        <form onSubmit={submit} className="mt-8 grid gap-5 bg-white border border-stone-200 p-8 rounded-md">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Business / Family / Group Name</label>
            <Input data-testid="wh-name-public" value={form.business_name} onChange={set("business_name")} placeholder="Sunrise Grocery, The Wilson Family, etc." className="mt-2 bg-stone-50" />
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Buyer Type</label>
              <Select value={form.business_type} onValueChange={set("business_type")}>
                <SelectTrigger data-testid="wh-type-public" className="mt-2 bg-stone-50"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="store">Store / Restaurant</SelectItem>
                  <SelectItem value="family">Family Group</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">State</label>
              <Select value={form.state} onValueChange={set("state")}>
                <SelectTrigger data-testid="wh-state-public" className="mt-2 bg-stone-50"><SelectValue placeholder="Select state" /></SelectTrigger>
                <SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Expected Weekly Volume</label>
              <Input data-testid="wh-volume-public" value={form.expected_volume} onChange={set("expected_volume")} placeholder="e.g., 200 lbs mixed greens, 50 dozen eggs" className="mt-2 bg-stone-50" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Contact Phone</label>
              <Input data-testid="wh-phone-public" value={form.contact_phone} onChange={set("contact_phone")} placeholder="(555) 555-0142" className="mt-2 bg-stone-50" />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Anything we should know?</label>
            <Textarea data-testid="wh-notes-public" rows={4} value={form.notes} onChange={set("notes")} placeholder="Categories you're most interested in, delivery preferences, frequency, etc." className="mt-2 bg-stone-50" />
          </div>

          <div>
            <Button data-testid="wh-submit-public" type="submit" disabled={submitting} className="bg-emerald-800 hover:bg-emerald-700 text-stone-50 px-7 py-6 rounded-md">
              {submitting ? "Submitting..." : user ? "Submit Application" : "Sign in & Apply"}
            </Button>
            {!user && (
              <p className="mt-3 text-xs text-stone-500 font-sans">You'll be asked to sign in with Google before your application is submitted.</p>
            )}
          </div>
        </form>

        <p className="mt-6 text-sm text-stone-500 font-sans">
          Questions first? <Link to="/contact" className="text-emerald-800 underline">Contact us</Link> and a team member will walk you through it.
        </p>
      </section>
    </div>
  );
}
